import React, { useState, useEffect } from 'react';
import { X, History, Calendar, MessageSquare, User, Clock, ChevronDown, ChevronUp, Star } from 'lucide-react';
import { api } from '../services/api';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ConversationHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerPhone: string;
  customerName?: string;
}

interface HistoryConversation {
  id: string;
  protocolId: string;
  customerName: string | null;
  customerPhone: string;
  status: string;
  createdAt: string;
  closedAt: string | null;
  department: {
    id: string;
    name: string;
  } | null;
  assignedAgent: {
    id: string;
    name: string;
    email: string;
  } | null;
  rating: {
    id: string;
    rating: string;
    comment: string | null;
  } | null;
  messageCount: number;
  messages: Array<{
    id: string;
    content: string;
    senderType: string;
    timestamp: string;
    agent?: {
      id: string;
      name: string;
      department: {
        name: string;
      } | null;
    } | null;
  }>;
}

export const ConversationHistoryModal: React.FC<ConversationHistoryModalProps> = ({
  isOpen,
  onClose,
  customerPhone,
  customerName,
}) => {
  const [history, setHistory] = useState<HistoryConversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedConversationId, setExpandedConversationId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadHistory();
    }
  }, [isOpen, customerPhone]);

  const loadHistory = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.getConversationHistory(customerPhone);
      setHistory(data);
    } catch (error: any) {
      console.error('Failed to load conversation history:', error);
      setError(error.response?.data?.error || 'Falha ao carregar histórico de conversas');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpand = (conversationId: string) => {
    setExpandedConversationId(
      expandedConversationId === conversationId ? null : conversationId
    );
  };

  const getRatingStars = (rating: string) => {
    const ratingMap: { [key: string]: number } = {
      'VERY_BAD': 1,
      'BAD': 2,
      'NEUTRAL': 3,
      'GOOD': 4,
      'EXCELLENT': 5,
    };
    return ratingMap[rating] || 0;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl transform transition-all w-full max-w-4xl z-50 max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <History className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Histórico de Conversas
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {customerName || customerPhone}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : error ? (
              <div className="p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
                {error}
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Nenhum histórico de conversas encontrado</p>
              </div>
            ) : (
              <div className="space-y-4">
                {history.map((conversation) => (
                  <div
                    key={conversation.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {/* Conversation Header */}
                    <div
                      className="bg-gray-50 dark:bg-gray-700/50 p-4 cursor-pointer"
                      onClick={() => toggleExpand(conversation.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-sm font-mono font-semibold">
                              #{conversation.protocolId}
                            </span>
                            {conversation.department && (
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {conversation.department.name}
                              </span>
                            )}
                            {conversation.assignedAgent && (
                              <span className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                                <User className="w-3 h-3" />
                                {conversation.assignedAgent.name}
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-4 flex-wrap text-sm text-gray-600 dark:text-gray-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {format(parseISO(conversation.createdAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                            </span>
                            {conversation.closedAt && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                Encerrado: {format(parseISO(conversation.closedAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-4 h-4" />
                              {conversation.messageCount} mensagens
                            </span>
                          </div>

                          {conversation.rating && (
                            <div className="flex items-center gap-2">
                              <div className="flex">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`w-4 h-4 ${
                                      conversation.rating && i < getRatingStars(conversation.rating.rating)
                                        ? 'text-yellow-400 fill-yellow-400'
                                        : 'text-gray-300 dark:text-gray-600'
                                    }`}
                                  />
                                ))}
                              </div>
                              {conversation.rating.comment && (
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                  "{conversation.rating.comment}"
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="ml-4">
                          {expandedConversationId === conversation.id ? (
                            <ChevronUp className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Messages */}
                    {expandedConversationId === conversation.id && (
                      <div className="p-4 bg-white dark:bg-gray-800 max-h-96 overflow-y-auto">
                        <div className="space-y-3">
                          {conversation.messages.map((message) => {
                            const isCustomer = message.senderType === 'CUSTOMER';
                            const isSystem = message.senderType === 'SYSTEM';

                            if (isSystem) {
                              return (
                                <div key={message.id} className="flex justify-center">
                                  <div className="bg-green-600 dark:bg-green-700 text-white text-xs px-4 py-2 rounded-lg max-w-[80%]">
                                    {message.content}
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <div
                                key={message.id}
                                className={`flex ${isCustomer ? 'justify-start' : 'justify-end'}`}
                              >
                                <div
                                  className={`max-w-[70%] rounded-lg px-3 py-2 ${
                                    isCustomer
                                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                                      : 'bg-blue-600 text-white'
                                  }`}
                                >
                                  {!isCustomer && message.agent && (
                                    <p className="text-xs opacity-75 mb-1 font-semibold">
                                      {message.agent.name}
                                      {message.agent.department && ` - ${message.agent.department.name}`}
                                    </p>
                                  )}
                                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                                  <p
                                    className={`text-xs mt-1 ${
                                      isCustomer ? 'text-gray-500' : 'text-blue-100'
                                    }`}
                                  >
                                    {format(parseISO(message.timestamp), 'HH:mm', { locale: ptBR })}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 dark:bg-gray-600 dark:hover:bg-gray-500 text-white rounded-lg transition"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
